<script>
  import { onMount } from "svelte";
  import { group, sum, range, descending } from "d3-array";
  import { format } from "d3-format";
  import mapToArray from "../utils/mapToArray.js";
  // import youtube from "../utils/youtube.js";
  import Meta from "./Meta.svelte";
  import Footer from "./pudding/Footer.svelte";
  import Histogram from "./Histogram.svelte";
  import Bar from "./Bar.svelte";
  import episodesRaw from "../data/episodes.csv";
  import captionsRaw from "../data/captions.csv";

  let innerH = 560;
  let videoEl;

  const episodes = episodesRaw.map((d) => ({
    ...d,
    index: +d.index,
    count: +d.count,
  }));

  const captions = captionsRaw.map((d) => ({
    ...d,
    index: +d.index,
    season: +d.season,
    episode: +d.episode,
    conseq: d.conseq.split("|").map((v) => +v),
    count: +d.count,
    offset: +d.offset,
    duration: +d.duration,
    // start: +d.start,
    // stop: +d.stop,
    // startPrev: +d.startPrev,
    // minute: Math.round(+d.start / 60),
  }));

  const captionsGrouped = group(captions, (d) => d.index);
  const byEpisode = episodes.map((d) => ({
    ...d,
    captions: captionsGrouped.has(d.index) ? captionsGrouped.get(d.index) : [],
  }));

  const total = sum(captions.map((d) => d.count));

  const cast = {};
  captions.forEach((d) => {
    let count = d.count;
    const person1 = cast[d.character];

    if (d.character_2) {
      count -= 1;
      const person2 = cast[d.character_2];
      if (person2) {
        person2.sequences += 1;
        person2.words += 1;
      } else {
        cast[d.character_2] = { sequences: 1, words: 1 };
      }
    }

    if (person1) {
      person1.words += count;
      person1.sequences += 1;
    } else {
      cast[d.character] = { sequences: 1, words: count };
    }
  });

  const castCount = [];
  Object.keys(cast).map((name) => {
    castCount.push({ name, ...cast[name] });
  });
  castCount.sort((a, b) => descending(a.words, b.words));

  const handleSeek = ({ detail }) => {
    videoEl.currentTime = detail;
    // if not playing, play
    if (videoEl.paused) videoEl.play();
  };
</script>

<svelte:window bind:innerHeight="{innerH}" />

<Meta />

<div class="story">
  <section class="histogram" style="height: {innerH}px;">
    <h1>Every time someone says <strong>“cool”</strong> in Community</h1>

    <div>
      <div id="media">
        <div id="player">
          <video
            src="assets/cool.mp4"
            poster="assets/cool.jpg"
            playsinline
            preload
            controls
            bind:this="{videoEl}"
          ></video>
        </div>
      </div>
      <Histogram data="{byEpisode}" on:seek="{handleSeek}" />
    </div>
  </section>

  <div class="prose">
    <p>
      Hi I’m
      <a href="https://pudding.cool/author/russell-samora">Russell</a>. After
      watching an early
      <a href="https://www.youtube.com/watch?v=O-y144qyUG0">supercut</a>
      of every time Abed says
      <strong>cool</strong>, I figured it was time to update and visualize. It
      turns out that the 412
      <strong>cools</strong>
      make it the second most used adjective, just behind
      <strong>good</strong>.
    </p>
    <p>
      The data were made from Netflix captions. Consecutive
      <strong>cools</strong>
      were found by stripping out all punctuation then determning how many times
      they appeared repeatedly.
    </p>
    <p>
      Data do not include 30 instances of derivatives, including
      <strong>coolest,</strong>
      <strong>cooler,</strong>
      <strong>cooled,</strong>
      <strong>coolness,</strong>
      <strong>cooling,</strong>
      <strong>coolsies,</strong>
      and
      <strong>coolios.</strong>
      The color scale is the cool interpolation of Niccoli’s perceptual rainbow.
    </p>
    <p>
      And if you were wondering if Abed really has the lion’s share of
      <strong>cools</strong>, here you go:
    </p>
  </div>

  <section class="bar">
    <h2><strong>“Cools”</strong> per character</h2>
    <div>
      <Bar data="{castCount}" col="{'words'}" />
    </div>
  </section>

  <section>
    <div class="prose">
      <p>
        If you thought that was cool, considering supporting The Pudding on
        <a href="https://patreon.com/thepudding">Patreon.</a>
        Get in touch at sup@pudding.cool.
      </p>
    </div>
  </section>
</div>

<Footer />

<style>
  div.story {
    padding: 0 0.75em;
  }

  section.histogram {
    min-height: 85vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-bottom: 2em;
    overflow: hidden;
  }

  section.bar {
    max-width: 40em;
    margin: 2em auto 4em auto;
  }

  h1 {
    font-size: 8vw;
    text-align: center;
    line-height: 1.2;
  }

  h2 {
    font-size: 1.5em;
    text-align: center;
  }

  .prose {
    max-width: 30em;
    margin: 0 auto;
  }

  .prose p {
    line-height: 1.6;
  }

  #media {
    position: absolute;
    z-index: var(--z-top);
    width: 90%;
    max-width: 480px;
    min-width: 280px;
    top: 18%;
    left: 50%;
    transform: translate(-50%, 0);
    padding: 0;
  }

  video {
    width: 100%;
    display: block;
  }

  @media only screen and (min-width: 480px) {
    h1 {
      font-size: 5vw;
      max-width: 560px;
      margin-left: auto;
      margin-right: auto;
    }
  }

  @media only screen and (min-width: 720px) {
    h1 {
      font-size: 4vw;
      max-width: 560px;
    }
  }

  @media only screen and (min-width: 960px) {
    h1 {
      font-size: 3.5vw;
      max-width: 100%;
    }

    div {
      padding: 0 2em;
    }

    div.story {
      padding: 0 0.75em;
    }

    #media {
      width: 45%;
      max-width: 480px;
      min-width: 300px;
      bottom: 40%;
      left: 9%;
      top: auto;
      transform: translate(0, 0);
    }
  }
</style>
